import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { USER_GUIDES, CATEGORY_COLORS } from '../../data/userGuides';
import { useGovernance } from '../../context/GovernanceContext';

function CategoryBadge({ category }) {
  const color = CATEGORY_COLORS[category] || '#6B7280';
  return (
    <span
      className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full text-white"
      style={{ backgroundColor: color }}
    >
      {category}
    </span>
  );
}

function DeployModal({ guide, clients, onClose, onDeploy }) {
  const [clientId, setClientId] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleDeploy = async () => {
    if (!clientId) return;
    setSaving(true);
    setError(null);
    try {
      await onDeploy(clientId, guide.slug, notes);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to deploy guide.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <h2 className="text-base font-bold text-brand-navy mb-1">Deploy to a client</h2>
        <p className="text-xs text-mid-grey mb-4">
          Add <span className="font-semibold">{guide.title}</span> to a client's guide plan.
        </p>

        <label className="block text-xs font-semibold text-brand-navy mb-1">Client</label>
        <select
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-brand-teal"
        >
          <option value="">Select a client…</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        <label className="block text-xs font-semibold text-brand-navy mb-1">Notes (optional)</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="e.g. Priority audience, delivery notes…"
          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-brand-teal resize-none"
        />

        {error && <p className="text-xs text-red-600 mb-3">{error}</p>}

        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="text-sm text-mid-grey hover:text-brand-navy px-3 py-1.5 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleDeploy}
            disabled={!clientId || saving}
            className="text-sm font-semibold bg-brand-navy text-white px-4 py-1.5 rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          >
            {saving ? 'Deploying…' : 'Deploy'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function GuideReader() {
  const { slug } = useParams();
  const { clients, deployGuide } = useGovernance();
  const [modalOpen, setModalOpen] = useState(false);

  const guide = USER_GUIDES.find((g) => g.slug === slug);

  if (!guide) {
    return (
      <div className="text-center py-16">
        <p className="text-mid-grey mb-2">Guide not found.</p>
        <Link to="/guides" className="text-brand-teal text-sm hover:underline">
          ← Back to Guide Library
        </Link>
      </div>
    );
  }

  return (
    <>
      {/* Print styles applied via Tailwind print utilities */}
      <div>
        {/* Breadcrumb — hidden on print */}
        <nav className="print:hidden flex items-center gap-2 text-sm text-mid-grey mb-5">
          <Link to="/guides" className="hover:text-brand-navy">Guides</Link>
          <span>/</span>
          <span className="text-brand-navy font-medium">{guide.title}</span>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setModalOpen(true)}
              className="text-xs font-semibold text-brand-navy border border-brand-navy rounded-lg px-3 py-1 hover:bg-brand-navy hover:text-white transition-colors"
            >
              Deploy to a client
            </button>
            <button
              onClick={() => window.print()}
              className="text-xs font-semibold text-brand-teal border border-brand-teal rounded-lg px-3 py-1 hover:bg-brand-teal hover:text-white transition-colors"
            >
              Print / Export
            </button>
          </div>
        </nav>

        {/* Guide header */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <CategoryBadge category={guide.category} />
            <span className="text-xs text-mid-grey bg-gray-100 px-2 py-0.5 rounded-full">
              {guide.audience}
            </span>
            <span className="text-xs text-mid-grey">{guide.readingTime} min read</span>
          </div>
          <h1 className="text-2xl font-bold text-brand-navy leading-snug">{guide.title}</h1>
          <p className="mt-2 text-sm text-mid-grey leading-relaxed max-w-2xl">{guide.summary}</p>
        </div>

        {/* Sections — prose layout */}
        <div className="max-w-2xl space-y-8">
          {guide.sections.map((section) => (
            <div key={section.heading}>
              <h2 className="text-base font-bold text-brand-navy mb-2">{section.heading}</h2>
              {section.body.split('\n\n').map((para, i) => (
                <p key={i} className="text-sm text-gray-700 leading-relaxed mb-3">
                  {para}
                </p>
              ))}
            </div>
          ))}
        </div>

        {/* Bottom actions — hidden on print */}
        <div className="print:hidden mt-10 pt-6 border-t border-gray-200 flex items-center gap-3">
          <Link to="/guides" className="text-xs text-mid-grey hover:text-brand-navy">
            ← Back to Guide Library
          </Link>
          <button
            onClick={() => setModalOpen(true)}
            className="ml-auto text-xs font-semibold text-brand-navy border border-brand-navy rounded-lg px-3 py-1 hover:bg-brand-navy hover:text-white transition-colors"
          >
            Deploy to a client
          </button>
        </div>
      </div>

      {modalOpen && (
        <DeployModal
          guide={guide}
          clients={clients}
          onClose={() => setModalOpen(false)}
          onDeploy={deployGuide}
        />
      )}
    </>
  );
}
